/* eslint-disable react/prop-types */
import { useEffect, useState, ReactNode } from "react";
import { createPortal } from "react-dom";

export type PortalProps = {
  containerId?: string;
  containerElement?: HTMLElement | null;
  children: ReactNode;
};

/**
 * Portal component that renders children into a DOM node outside of the parent component hierarchy
 *
 * @example
 * ```tsx
 * <div id="toolbar-container" />
 *
 * <COREPortal containerId="toolbar-container">
 *   <MyToolbar />
 * </COREPortal>
 * ```
 */
export const COREPortal: React.FC<PortalProps> = ({
  containerId,
  containerElement,
  children,
}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (containerElement) {
      setContainer(containerElement);
      return;
    }

    if (containerId) {
      const element = document.getElementById(containerId);

      if (!element) {
        console.warn(
          `Portal: Container with id "${containerId}" not found. Make sure the container exists before rendering the Portal.`
        );
      }

      setContainer(element);
    }
  }, [containerId, containerElement]);

  if (!container) return null;

  return createPortal(children, container);
};
